import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback } from 'react';
// hooks
import useLocalStorage from '../hooks/useLocalStorage'
// utils
import axios from '../utils/axios';
//
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  eventId: '',
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      eventId: action.payload.eventId ?? '',
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'SETUSER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'SETEVENTID') {
    return {
      ...state,
      eventId: action.payload.eventId ?? '',
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [user, changeUser] = useLocalStorage('user', initialState.user)
  const [eventId, changeEventId] = useLocalStorage('eventId', initialState.eventId)

  const initialize = useCallback(async () => {
    try {
      const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : '';
      const refreshToken = localStorage.getItem('refreshToken')

      if (accessToken) {
        // && isValidToken(accessToken) 
        setSession(accessToken, refreshToken);
        
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
            eventId,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false, 
            user: null,
            eventId,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = async (email, password) => {
    const { data } = await axios.post('/api/v1/auth/', {
      email,
      password,
    });
    const { access, refresh } = data;

    setSession(access,refresh);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {}
      },
    });

    return data
  };

  // REGISTER
  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;
    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  // LOGOUT
  const logout = async () => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  };

  // SETUSER
  /**
   * Membership data
   * Only users with property -> `is_member: true`
   * @param {Object} newUser 
   */
  const setUser = (newUser) => {
    try {
      changeUser(newUser ?? null)
    } finally {
      dispatch({
        type: 'SETUSER',
        payload: {
          user: newUser,
        },
      });
    }
  };

  // SETEVENTID
  /**
   * Event id 
   * @param {string} newEventId url param /:id
   */
  const setEventId = (newEventId = '') => {
    if (typeof eventId !== 'string') return
    try {
      changeEventId(newEventId)
    } finally {
      dispatch({
        type: 'SETEVENTID',
        payload: {
          eventId: newEventId,
        },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        // register,
        setUser,
        setEventId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
